import { useRef, useState, useCallback, useEffect } from 'react';
import { useWellSelectionStore } from '@cores/stores/useWellSelectionStore';
import SketchViewModel from '@arcgis/core/widgets/Sketch/SketchViewModel.js';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer.js';
import { initWellLayer } from '../utils/initWellLayer';
import { findLayerOrSublayer } from '../utils/mapUtils.js';

import { log } from '@libs/utils/helper.js';
import { LOG_MODULES, APP_CONFIG,SERVICE_KEYS,ALL_RECORDS } from '@config/config';

const LAYER_TITLE = APP_CONFIG.MAPS.Well.title;

export const useWellMapCore = () => {
  const [mapView, setMapView] = useState(null);
 
  const isInitialPositionSet = useRef(false);

  // ── Store (direct access, no prop drilling) ──────────────────────────────
  const checkedRds    = useWellSelectionStore((s) => s.checkedRds);
  const setCheckedRds = useWellSelectionStore((s) => s.setCheckedRds);
  const defExpress   = useWellSelectionStore((s) => s.defExpress); // ← here

  const defExpressRef = useRef(defExpress);
  const checkedRdsRef = useRef(checkedRds);
  useEffect(() => {
    checkedRdsRef.current = checkedRds;
  }, [checkedRds]);

  useEffect(() => {
    defExpressRef.current = defExpress;
  }, [defExpress]);      

  // ── Map ready ────────────────────────────────────────────────────────────
  const onarcgisViewReadyChange = useCallback((event) => {
    const view = event?.target?.view || event?.detail?.view || event?.view;
    const map  = event?.target?.map  || event?.detail?.map  || event?.map;
    log(LOG_MODULES.WELLARCGISMAPPER, 'MapView Ready');
    setMapView(view);

    const wellLayer = initWellLayer(map, view, isInitialPositionSet, defExpress); // ← passed as arg
    if (wellLayer) {
          wellLayer.when(() => {
              log(LOG_MODULES.WELLARCGISMAPPER, 'Well Layer and Renderer fully loaded');
          });
     }

  }, []);
    // ── Make Map Layer Reactive to Auth Changes ─────────────────────────────
  useEffect(() => {
    if (!mapView?.map) return;

    // Find the layer we created earlier
    const targetLayer = findLayerOrSublayer(mapView.map, LAYER_TITLE);
    
    // If the layer exists and defExpress has updated, apply the new filter
    if (targetLayer && defExpress[SERVICE_KEYS.NIOGEMS_MAP_SERVICES]) {
      targetLayer.definitionExpression = defExpress[SERVICE_KEYS.NIOGEMS_MAP_SERVICES];
      log(LOG_MODULES.WELLARCGISMAPPER, 'Updated Layer definitionExpression:', targetLayer.definitionExpression);
    }
  }, [defExpress, mapView]); 


  // ── Rectangle selection ──────────────────────────────────────────────────

  const sketchLayerRef = useRef(new GraphicsLayer({ id: 'selection-layer', listMode: 'hide' }));
  const sketchVMRef    = useRef(null);

  useEffect(() => {
    if (!mapView?.map || sketchVMRef.current) return;

    if (!mapView.map.findLayerById(sketchLayerRef.current.id)) {
      mapView.map.add(sketchLayerRef.current);
    }

    const vm = new SketchViewModel({
      view: mapView,
      layer: sketchLayerRef.current,
      updateOnGraphicClick: false,
      polygonSymbol: {
        type: 'simple-fill',
        color: [0, 122, 194, 0.1],
        outline: { color: [0, 122, 194], width: 2 },
      },
    });

    vm.on('create', async (event) => {
      if (event.state !== 'complete') return;
      log(LOG_MODULES.MAP_SELECTION, 'Rectangle complete. Querying features...');

      const targetLayer = findLayerOrSublayer(mapView.map, LAYER_TITLE);
      if (!targetLayer) return;

      
      const QueryId =APP_CONFIG.MAPS.Well.queryField;

      const query = targetLayer.createQuery();
      query.geometry = event.graphic.geometry;
      query.spatialRelationship = 'contains';
      query.outFields = [QueryId];
      query.where = defExpressRef.current ?? ALL_RECORDS; // ← AND reservation filter

      try {
        const results   = await targetLayer.queryFeatures(query);

        // Clean the IDs before they enter the store
        const newApiIds = results.features.map((f) => {
                  let val = f.attributes[QueryId];
                  // If it's the WELL_ID field, strip the braces immediately
                  if (QueryId === APP_CONFIG.MAPS.Well.specialWellId && typeof val === "string") {
                    val = val.replace(/[{}]/g, "");
                  } 
                  return val;
        });



        if (newApiIds.length > 0) {
          const merged = Array.from(new Set([...checkedRdsRef.current, ...newApiIds]));
          setCheckedRds(merged);            // ← straight to store
        }
        sketchLayerRef.current.removeAll();
      } catch (err) {
        console.error('Selection query failed:', err);
      }
    });

    sketchVMRef.current = vm;

    return () => {
      sketchVMRef.current?.destroy();
      mapView.map?.remove(sketchLayerRef.current);
    };
  }, [mapView, setCheckedRds]);

  const activateRectangle = useCallback(() => {
    sketchVMRef.current
      ? sketchVMRef.current.create('rectangle')
      : log(LOG_MODULES.MAP_SELECTION, 'SketchViewModel not ready.');
  }, []);

  return { mapView, onarcgisViewReadyChange, activateRectangle };
};